import {
  type ColumnDef,
  createColumnHelper,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format, formatDistanceToNow } from 'date-fns'
import {
  Activity,
  ChevronDown,
  ChevronRight,
  Database,
  Info,
  Loader2,
  Plus,
  RefreshCw,
  User,
  XCircle,
  Zap
} from 'lucide-react'
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { adminMedicalPlanDataJobs } from 'rails_routes'
import { Badge, Button, Select, Tooltip, TooltipProvider } from 'slab'
import { formatDurationBetweenDates } from 'utils'
import { useGetAdminMedicalPlanIngestions, useGetAdminMedicalPlanIngestionsFilters } from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

import { ExpandedDataIngestionRow } from './ExpandedDataIngestionRow'
import { IngestionJobProgressBar } from './IngestionJobProgressBar'
import { IngestionJobStatusBadge } from './IngestionJobStatusBadge'
import { type IngestionJob } from './types'
import LoadingSpinnerCentered from '../../reusable/LoadingSpinnerCentered'

const MAX_JOBS_PER_PAGE = 10
const REFRESH_INTERVAL = 10000 // 10 seconds

interface FilterOptions {
  years?: number[]
  states?: Array<{ code: string; name: string }>
  statuses?: Array<{ value: string; label: string }>
}

const useURLFilters = () => {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    return {
      year: params.get('year') ? parseInt(params.get('year')!) : undefined,
      status: params.get('status') || undefined,
      state: params.get('state') || undefined,
    }
  }, [])
}

export const MedicalPlanIngestionTable: React.FC = () => {
  const { assign } = useWindowLocation()

  const initialFilters = useURLFilters()
  const [selectedYear, setSelectedYear] = useState<number | undefined>(initialFilters.year)
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(initialFilters.status)
  const [selectedState, setSelectedState] = useState<string | undefined>(initialFilters.state)
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [currentPage, setCurrentPage] = useState(1)

  // Fetch available filter options
  const { data: filterOptions } = useGetAdminMedicalPlanIngestionsFilters() as { data?: FilterOptions }

  const { data, isLoading, refetch, dataUpdatedAt } = useGetAdminMedicalPlanIngestions({
    ...(selectedYear && { year: selectedYear }),
    ...(selectedStatus && { status: selectedStatus }),
    ...(selectedState && { state_code: selectedState }),
    'page[number]': currentPage,
    'page[size]': MAX_JOBS_PER_PAGE,
  }, {
    query: {
      refetchInterval: (query) => {
        // Only refetch if there are active jobs
        const jobs = (query.state.data?.jobs || []) as IngestionJob[]
        const hasActive = jobs.some(job => job.status === 'queued' || job.status === 'running')
        return hasActive ? REFRESH_INTERVAL : false
      },
      refetchIntervalInBackground: false, // Stop refetching when tab is not active
    },
  })

  const jobs = (data?.jobs || []) as IngestionJob[]
  const pagination = data?.pagination

  // Check if any jobs are actively running
  const hasActiveJobs = useMemo(() => {
    return jobs.some(job => job.status === 'queued' || job.status === 'running')
  }, [jobs])

  const columnHelper = createColumnHelper<IngestionJob>()

  const columns = useMemo<ColumnDef<IngestionJob, any>[]>(() => [
    columnHelper.display({
      id: 'expander',
      header: () => null,
      cell: ({ row }) => (
        <button
          onClick={row.getToggleExpandedHandler()}
          className='p-1 hover:bg-gray-100 rounded transition-all duration-200 hover:scale-110'
          aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
        >
          {row.getIsExpanded()
            ? (
              <ChevronDown className='w-3 h-3 text-gray-400' />
              )
            : (
              <ChevronRight className='w-3 h-3 text-gray-400' />
              )}
        </button>
      ),
    }),
    columnHelper.accessor('year', {
      header: 'Plan Year',
      cell: ({ getValue }) => {
        const value = getValue()
        return <span className='text-sm text-gray-600'>{value}</span>
      },
    }),
    columnHelper.accessor('trigger_type', {
      header: 'Type',
      cell: ({ getValue, row }) => {
        const type = getValue()
        const icon = type === 'manual' ? <User className='w-3 h-3' /> : <Zap className='w-3 h-3' />
        const badgeType = type === 'manual' ? 'neutral' : 'slate'

        return (
          <TooltipProvider>
            <Tooltip
              content={
                <div className='text-xs'>
                  <div className='font-medium'>{type === 'manual' ? 'Manual' : 'Automated'}</div>
                  {row.original.triggered_by && (
                    <div className='mt-1'>Triggered by: {row.original.triggered_by}</div>
                  )}
                </div>
              }
            >
              <div className='flex items-center gap-1'>
                {icon}
                <Badge type={badgeType}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </Badge>
              </div>
            </Tooltip>
          </TooltipProvider>
        )
      },
    }),
    columnHelper.accessor('started_at', {
      header: 'Started',
      cell: ({ getValue }) => {
        const value = getValue()
        return (
          <div className='text-sm text-gray-600'>
            {value
              ? (
                <Tooltip content={format(new Date(value), 'MMM d, yyyy HH:mm:ss')}>
                  <span className='cursor-help'>
                    {formatDistanceToNow(new Date(value), { addSuffix: true })}
                  </span>
                </Tooltip>
                )
              : (
                <span className='text-gray-400'>Not started</span>
                )}
          </div>
        )
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => <IngestionJobStatusBadge status={getValue()} />,
    }),
    columnHelper.accessor('total_states', {
      header: 'Progress',
      cell: ({ row }) => {
        const job = row.original
        const expectedCount = job.expected_states_count || job.total_states
        const hasFailures = job.failed_count > 0

        return (
          <div className='space-y-1'>
            <div className='flex items-center gap-2'>
              <span className='text-xs text-gray-500'>
                {job.total_states} / {expectedCount} states
              </span>
              {hasFailures && (
                <Tooltip content={`${job.failed_count} state(s) failed`}>
                  <div className='flex items-center gap-1'>
                    <XCircle className='w-3 h-3 text-red-500' />
                    <span className='text-xs text-red-600 font-medium'>{job.failed_count}</span>
                  </div>
                </Tooltip>
              )}
            </div>
            <div className='w-24 sm:w-32 md:w-48'>
              <IngestionJobProgressBar job={job} />
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('completed_at', {
      header: 'Duration',
      cell: ({ getValue, row }) => {
        const completed = getValue()
        const started = row.original.started_at

        if (!started) return <span className='text-gray-400 text-sm'>-</span>

        if (!completed) {
          return (
            <div className='flex items-center gap-1 text-sm text-yellow-600'>
              <Loader2 className='w-3 h-3 animate-spin' />
              <span>In progress</span>
            </div>
          )
        }

        const durationText = formatDurationBetweenDates(started, completed)

        return <span className='text-sm text-gray-600'>{durationText}</span>
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const handleViewDetails = () => {
          // Expand the row to show details
          row.toggleExpanded()
        }

        return (
          <div className='flex items-center gap-2'>
            <Button
              size='small'
              variant='secondary'
              leftIcon={row.getIsExpanded() ? ChevronDown : ChevronRight}
              onClick={handleViewDetails}
            >
              {row.getIsExpanded() ? 'Hide Details' : 'View Details'}
            </Button>
          </div>
        )
      },
    }),
  ], [])

  const table = useReactTable({
    data: jobs,
    columns,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
  })

  useEffect(() => {
    // Reset to first page when filters change
    setCurrentPage(1)
    setExpanded({})
  }, [selectedYear, selectedStatus, selectedState])

  const handleYearChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    const year = value ? parseInt(value) : undefined
    const url = new URL(window.location.href)
    if (year !== undefined) {
      url.searchParams.set('year', String(year))
    } else {
      url.searchParams.delete('year')
    }
    window.history.pushState({}, '', url)
    setSelectedYear(year)
  }, [])

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    const url = new URL(window.location.href)
    if (value) {
      url.searchParams.set('status', value)
    } else {
      url.searchParams.delete('status')
    }
    window.history.pushState({}, '', url)
    setSelectedStatus(value || undefined)
  }, [])

  const handleStateChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    const url = new URL(window.location.href)
    if (value) {
      url.searchParams.set('state', value)
    } else {
      url.searchParams.delete('state')
    }
    window.history.pushState({}, '', url)
    setSelectedState(value || undefined)
  }, [])

  const handleManualRefresh = useCallback(() => {
    refetch()
    toast.success('Data refreshed')
  }, [refetch])

  if (isLoading && !jobs.length) {
    return (
      <div className='flex flex-col justify-center items-center h-64'>
        <div className='mb-8 text-gray-500'>
          Loading medical plan ingestion history...
        </div>
        <LoadingSpinnerCentered height='50px' width='50px' />
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center'>
          <div className='flex space-x-2 items-center'>
            <Database className='w-6 h-6 sm:w-8 sm:h-8' />
            <h1 className='text-xl sm:text-2xl font-bold'>Medical Plan Ingestion Status</h1>
          </div>

          {/* Quick action button */}
          <Button
            variant='secondary'
            leftIcon={Plus}
            onClick={() => { assign(adminMedicalPlanDataJobs.index()) }}
            aria-label='Medical Plan Data Status Page'
          >
            Trigger New Ingestions
          </Button>
        </div>

        {/* Info text with navigation */}
        <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
          <div className='flex items-start gap-3'>
            <Info className='w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0' />
            <div className='space-y-2 text-sm'>
              <p className='text-gray-700'>
                <span className='font-medium'>Track ingestion history:</span> View the complete history and status of all medical plan data ingestion jobs below.
              </p>
              <p className='text-gray-700'>
                <span className='font-medium'>Start new ingestions:</span> To check updated data availability or trigger new ingestion jobs, visit the{' '}
                <a href={adminMedicalPlanDataJobs.index()} className='text-purple-600 hover:text-purple-800 underline font-medium'>
                  Medical Plan Data page
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
          {/* Filter selectors */}
          <div className='flex flex-wrap items-center gap-2 sm:gap-4'>
            {/* Year filter */}
            <div className='flex flex-col gap-1'>
              <label className='text-xs font-medium text-gray-700'>Plan Year</label>
              <Select
                value={selectedYear?.toString() || ''}
                onChange={handleYearChange}
                className='w-28 sm:w-32'
              >
                <option value=''>All Years</option>
                {(filterOptions?.years || []).map((year) => (
                  <option key={year} value={year.toString()}>
                    {year}
                  </option>
                ))}
              </Select>
            </div>

            {/* Status filter */}
            <div className='flex flex-col gap-1'>
              <label className='text-xs font-medium text-gray-700'>Status</label>
              <Select
                value={selectedStatus || ''}
                onChange={handleStatusChange}
                className='w-32 sm:w-40'
              >
                <option value=''>All Statuses</option>
                {filterOptions?.statuses?.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </Select>
            </div>

            {/* State filter */}
            <div className='flex flex-col gap-1'>
              <label className='text-xs font-medium text-gray-700'>State</label>
              <Select
                value={selectedState || ''}
                onChange={handleStateChange}
                className='w-36 sm:w-48'
              >
                <option value=''>All States</option>
                {filterOptions?.states?.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name} ({state.code})
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Polling status indicator */}
          <div className='flex items-center gap-3'>
            <Tooltip content={dataUpdatedAt
              ? `Last updated: ${formatDistanceToNow(dataUpdatedAt, { addSuffix: true })}`
              : 'No data yet'}
            >
              {hasActiveJobs
                ? (
                  <div className='flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg animate-pulse'>
                    <Activity className='w-4 h-4 text-green-600 animate-pulse' />
                    <span className='text-sm text-green-700 font-medium'>Live - Polling active jobs</span>
                  </div>
                  )
                : (
                  <Button
                    variant='tertiary'
                    size='small'
                    onClick={handleManualRefresh}
                    leftIcon={RefreshCw}
                    aria-label='Refresh data manually'
                  >
                    Refresh
                  </Button>
                  )}
            </Tooltip>

          </div>
        </div>

        {/* Table */}
        <div className='overflow-x-auto border border-gray-200 rounded-lg'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className='px-3 py-3 sm:px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap'
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {isLoading
                ? (
                  <tr>
                    <td colSpan={columns.length} className='px-6 py-4 text-center'>
                      <LoadingSpinnerCentered height='30px' width='30px' />
                    </td>
                  </tr>
                  )
                : table.getRowModel().rows.length === 0
                  ? (
                    <tr>
                      <td colSpan={columns.length} className='px-6 py-4 text-center text-gray-500'>
                        No ingestion jobs found for this year
                      </td>
                    </tr>
                    )
                  : (
                      table.getRowModel().rows.map((row) => (
                        <Fragment key={row.id}>
                          <tr className='hover:bg-gray-50'>
                            {row.getVisibleCells().map((cell) => (
                              <td
                                key={cell.id}
                                className='px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900'
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                          {row.getIsExpanded() && (
                            <tr id={`expanded-${row.id}`}>
                              <td colSpan={columns.length}>
                                <ExpandedDataIngestionRow job={row.original} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))
                    )}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination && (
            <div className='bg-white px-4 py-3 border-t border-gray-200 sm:px-6'>
              <div className='flex items-center justify-between'>
                <div className='text-sm text-gray-700'>
                  Showing page {(pagination as any).current_page} of {(pagination as any).total_pages}
                  {' '}({(pagination as any).total_count} total {(pagination as any).total_count === 1 ? 'job' : 'jobs'})
                </div>
                <div className='flex gap-2'>
                  <Button
                    variant='tertiary'
                    size='small'
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={(pagination as any).current_page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant='tertiary'
                    size='small'
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={(pagination as any).current_page === (pagination as any).total_pages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}

export default MedicalPlanIngestionTable
